import React, { useState } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  MessageSquarePlus,
  X,
  Bug,
  Lightbulb,
  Heart,
  HelpCircle,
  MoreHorizontal,
  Star,
  Send,
  CheckCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const FEEDBACK_TYPES = [
  { id: 'bug', label: 'Feil/Bug', icon: Bug, color: 'bg-red-600 hover:bg-red-700', description: 'Rapporter en feil' },
  { id: 'suggestion', label: 'Forslag', icon: Lightbulb, color: 'bg-yellow-600 hover:bg-yellow-700', description: 'Foreslå forbedring' },
  { id: 'praise', label: 'Ros', icon: Heart, color: 'bg-pink-600 hover:bg-pink-700', description: 'Del det du liker' },
  { id: 'question', label: 'Spørsmål', icon: HelpCircle, color: 'bg-blue-600 hover:bg-blue-700', description: 'Still et spørsmål' },
  { id: 'other', label: 'Annet', icon: MoreHorizontal, color: 'bg-gray-600 hover:bg-gray-700', description: 'Annen tilbakemelding' }
];

const FeedbackWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: type selection, 2: message, 3: success
  const [selectedType, setSelectedType] = useState(null);
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const resetForm = () => {
    setStep(1);
    setSelectedType(null);
    setMessage('');
    setRating(0);
    setError(null);
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(resetForm, 300);
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      setError('Vennligst skriv en melding');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Get token from localStorage if available
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.post(`${API}/feedback`, {
        feedback_type: selectedType.id,
        message: message.trim(),
        page_url: window.location.href,
        rating: rating > 0 ? rating : null,
        browser_info: navigator.userAgent
      }, { headers });

      setStep(3);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Kunne ikke sende tilbakemelding. Prøv igjen.');
    } finally {
      setSubmitting(false);
    }
  };

  const SelectedIcon = selectedType?.icon || MessageSquarePlus;

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 left-6 z-50 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg transition-all duration-300 ${
          isOpen 
            ? 'opacity-0 pointer-events-none scale-90' 
            : 'opacity-100 scale-100 hover:scale-105'
        } bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700`}
        data-testid="feedback-button"
      >
        <MessageSquarePlus className="w-5 h-5" />
        <span className="font-medium">Gi tilbakemelding</span>
      </button>

      {/* Feedback Panel */}
      <div
        className={`fixed bottom-6 left-6 z-50 w-96 max-w-[calc(100vw-3rem)] transition-all duration-300 ${
          isOpen 
            ? 'opacity-100 translate-y-0 pointer-events-auto' 
            : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SelectedIcon className="w-5 h-5 text-white" />
              <h3 className="text-white font-semibold">
                {step === 1 && 'Gi tilbakemelding'}
                {step === 2 && selectedType?.label}
                {step === 3 && 'Takk!'}
              </h3>
            </div>
            <button
              onClick={handleClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Step 1: Type Selection */}
            {step === 1 && (
              <div className="space-y-2">
                <p className="text-gray-400 text-sm mb-4">
                  Hva slags tilbakemelding vil du gi?
                </p>
                {FEEDBACK_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      onClick={() => handleTypeSelect(type)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${type.color} text-white`}
                      data-testid={`feedback-type-${type.id}`}
                    >
                      <Icon className="w-5 h-5" />
                      <div className="text-left">
                        <div className="font-medium">{type.label}</div>
                        <div className="text-xs opacity-80">{type.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Step 2: Message Input */}
            {step === 2 && (
              <div className="space-y-4">
                <button
                  onClick={() => setStep(1)}
                  className="text-gray-400 text-sm hover:text-white flex items-center gap-1"
                >
                  ← Tilbake
                </button>

                <div>
                  <label className="text-gray-300 text-sm block mb-2">
                    Din tilbakemelding
                  </label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={
                      selectedType?.id === 'bug' 
                        ? 'Beskriv feilen du opplevde...' 
                        : selectedType?.id === 'suggestion'
                        ? 'Del ditt forslag til forbedring...'
                        : selectedType?.id === 'praise'
                        ? 'Fortell oss hva du liker...'
                        : selectedType?.id === 'question'
                        ? 'Still ditt spørsmål...'
                        : 'Skriv din tilbakemelding...'
                    }
                    className="bg-gray-800 border-gray-600 text-white placeholder:text-gray-500 min-h-[120px]"
                    data-testid="feedback-message"
                  />
                </div>

                {/* Rating */}
                <div>
                  <label className="text-gray-300 text-sm block mb-2">
                    Vurdering (valgfritt)
                  </label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoveredRating(star)}
                        onMouseLeave={() => setHoveredRating(0)}
                        className="p-1 transition-transform hover:scale-110"
                      >
                        <Star
                          className={`w-6 h-6 ${
                            star <= (hoveredRating || rating)
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-gray-600'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {error && (
                  <p className="text-red-400 text-sm">{error}</p>
                )}

                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                  data-testid="feedback-submit"
                >
                  {submitting ? (
                    'Sender...'
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send tilbakemelding
                    </>
                  )}
                </Button>

                <p className="text-gray-500 text-xs text-center">
                  Siden: {window.location.pathname}
                </p>
              </div>
            )}

            {/* Step 3: Success */}
            {step === 3 && (
              <div className="text-center py-6">
                <div className="w-16 h-16 mx-auto mb-4 bg-green-600/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h4 className="text-white font-semibold text-lg mb-2">
                  Takk for tilbakemeldingen!
                </h4>
                <p className="text-gray-400 text-sm mb-6">
                  Vi setter stor pris på at du tar deg tid til å hjelpe oss med å forbedre tjenesten.
                </p>
                <Button
                  onClick={handleClose}
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:bg-gray-800"
                >
                  Lukk
                </Button>
              </div>
            )}
          </div>

          {/* Footer */}
          {step !== 3 && (
            <div className="px-4 pb-4">
              <p className="text-gray-500 text-xs text-center">
                🧪 Testfase - all feedback hjelper oss!
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Backdrop for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={handleClose}
        />
      )}
    </>
  );
};

export default FeedbackWidget;
